import { Autocomplete, SxProps, TextField, Theme } from "@mui/material";
import { useMemo } from "react";

import { GqlFilterOptionMultiSelect } from "~/__generated__";
import useDebouncedState from "~/shared/hooks/useDebouncedState";

import useAutocompleteGetOptions from "../../api/useAutocompleteGetOptions";

type Option = { label: string; value: string };

type Props = {
    sx?: SxProps<Theme>;
    option: GqlFilterOptionMultiSelect;
    value: string;
    extraParams?: Record<string, string>;
    onChange: (value: string | null) => void;
};

export default function MultiselectFilter({ sx, option, value, extraParams, onChange }: Props) {
    const [searchQuery, debouncedSearchQuery, setSearchQuery] = useDebouncedState("");
    const normalizedValue = value && typeof value === "string" ? value.split(",") : Array.isArray(value) ? value : [];

    const { data, selectedData, loading } = useAutocompleteGetOptions({
        selectedIds: normalizedValue,
        model: option.dataModel,
        q: debouncedSearchQuery,
        params: extraParams,
    });

    const selectedValue = useMemo(() => {
        const selectedIds = [...new Set(normalizedValue.filter(Boolean))];
        const nextSelectedValue = (selectedData ?? []).filter(o => selectedIds.includes(o.value));

        return nextSelectedValue;
    }, [selectedData, normalizedValue]);

    const handleOnchange = (nextValue: Option[]) => {
        if (nextValue.length === 0) {
            onChange(null);
        } else {
            onChange(nextValue.map(item => item.value).join(","));
        }
    };

    return (
        <Autocomplete
            sx={sx}
            multiple
            options={data ?? []}
            loading={loading}
            getOptionKey={option => option.value}
            getOptionLabel={option => option.label}
            value={selectedValue}
            onChange={(_, newValue) => handleOnchange(newValue)}
            inputValue={searchQuery}
            onInputChange={(_, newInputValue, reason) => {
                if ("reset" === reason) {
                    return;
                }

                setSearchQuery(newInputValue);
            }}
            renderInput={params => <TextField {...params} fullWidth variant="outlined" label={option.label} />}
        />
    );
}
